import {Fragment, ReactElement, useEffect, useState} from "react"
import {observer} from "mobx-react-lite"
import classNames from "classnames"
import {groupBy, merge} from "lodash-es"
import {Avatar, Button, Input} from "antd"
import {CloudUploadOutlined, ExclamationCircleOutlined} from "@ant-design/icons"
import {WKT} from "ol/format"
import {Point} from "ol/geom"
import {Coordinate} from "ol/coordinate"
import {t} from "@ui/common/intl"
import {useDidMountEffect} from "@ui/common/utils"
import {correct, load, mark, publish, TFullPoint, TPoint} from "@ui/api/poi"
import {PoiCategory} from "@ui/stores/service/poi"
import {useStore} from "@ui/stores"
import mapService from "@ui/services/MapService"
import Loader from "@ui/components/common/Loader"
import PoolModal from "@ui/components/common/PoolModal"
import SwipePanel from "@ui/components/services/common/SwipePanel"
import LeftIcon from "@ui/icons/common/Left"
import EditIcon from "@ui/icons/common/Edit"
import AimIcon from "@ui/icons/common/Aim"
import BookmarkIcon from "@ui/icons/track/Bookmark"
import BookmarkOffIcon from "@ui/icons/track/BookmarkOff"
import {PoiPage, TPoiPageProps} from "./"

const View = ({isShow, close, goto, param, notifyError}: TPoiPageProps): ReactElement => {
  const {poi, settings, movement} = useStore()
  const [loading, setLoading] = useState(false)
  const [init, setInit] = useState<{id: number}>(param)
  const [point, setPoint] = useState<TFullPoint>()
  const [dialog, setDialog] = useState(false)
  const [send, setSend] = useState("")
  const [sending, setSending] = useState(false)

  const isUser = point?.props.type === PoiCategory.USER
  const groupedPoint = groupBy(point?.props.values, ({code}) => code)

  useEffect(() => {
    return () => poi.unselect()
  }, [])

  useEffect(() => {
    setLoading(true)
    load(init.id)
      .then((data) => {
        setPoint(data)
        poi.selected !== init.id && poi.select(init.id)
        // moveToPoint(
        //   (new WKT().readGeometry(data.geometry) as Point).getCoordinates(),
        //   () => poi.selected !== init.id && poi.select(init.id)
        // )
      })
      .catch((error) => notifyError("poi.load.point.error", error))
      .finally(() => setLoading(false))
  }, [init])

  useDidMountEffect(() => {
    poi.selected
      ? init.id !== poi.selected && setInit({id: poi.selected})
      : goto(PoiPage.Main)
  }, [poi.selected])

  const moveToPoint = (coord: Coordinate, callback?: (isSuccess: boolean) => void) => {
    const padding: [number, number, number, number] = settings.isMobile
      ? [0.25, 0.5, 0.75, 0.5]
      : settings.isDesktop
        ? [0.5, 0.4, 0.5, 0.6]
        : [0.5, 0.35, 0.5, 0.65]
    movement.isFollowing && movement.switchFollowing()
    setTimeout(() => mapService.moveTo(coord as [number, number], {padding, callback}), 300)
  }

  const handleBack = () => {
    goto(PoiPage.Main)
  }

  const handleEdit = () => {
    if (point) {
      const param: TPoint = {
        id: point.props.id,
        point: (new WKT().readGeometry(point?.geometry) as Point).getCoordinates(),
        props: {
          color: point.props.color,
          title: point.props.values.find(({code}) => code === "title")?.value || "",
          more: point.props.values.find(({code}) => code === "more")?.value || null,
        }
      }
      goto(PoiPage.Save, param)
    } else {
      notifyError("poi.view.no.point.error")
      goto(PoiPage.Main)
    }
  }

  const handleCenter = () => {
    moveToPoint((new WKT().readGeometry(point?.geometry) as Point).getCoordinates())
  }

  const handleMark = () => {
    setLoading(true)
    mark(point!.props.id)
      .then(({favorite}) => {
        setPoint(point => merge(point, {props: {favorite}}))
        poi.update(point?.props.id!, {favorite})
      })
      .catch((error) => notifyError("poi.mark.point.error", error))
      .finally(() => setLoading(false))
  }

  const handleClose = () => {
    close()
    goto(PoiPage.Main)
    poi.filter.clear()
  }

  const handleSend = () => {
    if (point?.props.id) {
      setSending(true);
      (isUser ? publish : correct)(point.props.id, send)
        .then(() => handleCancel())
        .catch((error) => notifyError(`poi.${isUser ? "correct" : "publish"}.info.error`, error))
        .finally(() => {
          setSending(false)
        })
    }
  }

  const handleCancel = () => {
    setSend("")
    setDialog(false)
  }

	return isShow ? (
    <SwipePanel
      onClose={handleClose}
      onSwipe={close}
      className="view"
      header={<Button className="button-back" icon={<LeftIcon/>} onClick={handleBack}/>}
    >
      <Fragment>
        {loading && <Loader />}
        <div className="point">
          <div className="point-data">
            <div className="title">
              {point?.props.type && (
                <Avatar
                  shape="circle"
                  src={`/poi/${point?.props.type}.svg`}
                  className={classNames("point-icon", `color-${point?.props.color || 0}`)}
                />
              )}
              <span>
                {point?.props.values.find(({code}) => code === "title")?.value}
              </span>
            </div>
            <div className="text">
              {
                [...new Set(point?.props.values.map(({code}) => code))].map((code, i) => {
                  const key= `point-${point?.props.id}-row-${code}-${i}`
                  if (code === "title") return <Fragment key={key}></Fragment>
                  const row = groupedPoint[code]
                  return (
                    <div className={`block ${code}`} key={key} >
                      <div className="row code">
                        {t(`poi.view.point.props.code.${code}`)}
                      </div>
                      <div className="row value">
                        {row.map(({value, format}, j) => {
                          const key = `point-${point?.props.id}-value-${code}-${i}-${j}`
                           return format === "url"
                             ? <a target="_blank" href={value.includes("http") ? value : `https://${value}`} key={key}>{value}</a>
                             : <p key={key}>{value}</p>
                          }
                        )}
                      </div>
                    </div>
                  )
                })
              }
            </div>
          </div>
          {isUser && (
            <div className="actions">
              <Button icon={<EditIcon/>} onClick={handleEdit} type="text"/>
            </div>
          )}
        </div>
        {point && (
          <div className="buttons">
            <Button
              icon={<AimIcon/>}
              onClick={handleCenter}
            />
            <Button
              icon={point?.props.favorite ? <BookmarkIcon/> : <BookmarkOffIcon/>}
              onClick={handleMark}
            />
            <Button
                className={isUser ? "button-publish" : "button-error"}
                icon={isUser ? <CloudUploadOutlined /> : <ExclamationCircleOutlined />}
                onClick={() => setDialog(true)}
              >
                {t(`poi.view.${isUser ? "publish" : "error"}.button`)}
              </Button>
          </div>
        )}
        <PoolModal
          open={dialog}
          closable={false}
          title={t(`poi.view.modal.${isUser ? "publish" : "error"}.title`)}
          width=""
          centered={true}
          footer={
            <div className="buttons">
              <Button disabled={!send || sending} className="button-primary" onClick={handleSend}>
                {sending ? <Loader /> : t("button.send")}
              </Button>
              <Button disabled={sending} className="button-regular" onClick={handleCancel}>
                {t("button.cancel")}
              </Button>
            </div>
          }
        >
          <Fragment>
            <Input.TextArea
              onChange={(e) => setSend(e.target.value)}
              rows={6}
              placeholder={t(`poi.view.modal.info.${isUser ? "publish" : "error"}.placeholder`)}
              maxLength={1000}
              value={send}
            />
          </Fragment>
      </PoolModal>
      </Fragment>
    </SwipePanel>
  ) : (<></>)
}

export default observer(View)
